import { useEffect, useMemo, useState } from 'react';
import {
  COMPARE_OPTIONS,
  toDashboardCompare,
  toDashboardDatePreset,
  useGlobalFilter,
} from '../global-filter';
import { useShell } from '../shell/ShellProvider';
import { DailyBriefPanel, FocusList, useDailyBrief } from './daily-brief';
import { ExecutiveKpiStrip, useExecutiveKpis } from './executive-kpis';
import { MasterOverlayPanel, useMasterOverlay } from './master-overlay';
import { SalesPulse24Hours, useSalesPulse } from './sales-pulse-24h';
import { OpsIntelligencePanel, useOpsIntelligence } from './ops-intelligence';
import { IndiaBusinessMap, useIndiaMap } from './india-map';
import { DebugExportBar } from './debug-export/DebugExportBar';
import { RestBlocksGrid } from './rest-blocks/RestBlocksGrid';
import { useRestBlocks } from './rest-blocks/useRestBlocks';
import { pullDueOrderDomains, shouldPullDueOrderDomains } from './refreshOrders';

/**
 * Permanent Founder Dashboard container.
 * Business health → snapshot → KPIs → actions → trend → pulse → geography.
 * Context (store, marketplace, connection, period, compare) is owned by the shell
 * context strip; refresh and trust are owned by the shell top bar. This surface
 * renders business only.
 */
export function FounderDashboardFrame() {
  const { setContextLabels, setConnectionOptions, refreshToken } = useShell();
  const { context: filters, setFilters } = useGlobalFilter();
  const datePreset = toDashboardDatePreset(filters.dateRange);
  const compare = toDashboardCompare(filters.comparePeriod);
  const [liveMsg, setLiveMsg] = useState('');
  const [orderEpoch, setOrderEpoch] = useState(0);

  const brief = useDailyBrief(orderEpoch);
  const connection = brief.connections.find((c) => c.id === brief.connectionId) ?? null;
  const executiveKpis = useExecutiveKpis({
    refreshToken: orderEpoch,
    connectionId: brief.connectionId,
    connection,
    datePreset,
    compare,
  });
  const overlay = useMasterOverlay({
    refreshToken: orderEpoch,
    connectionId: brief.connectionId,
    connection,
    datePreset,
    compare,
  });
  const salesPulse = useSalesPulse({ refreshToken: orderEpoch, connection });
  const opsIntel = useOpsIntelligence({ refreshToken: orderEpoch, connection });
  const indiaMap = useIndiaMap({ refreshToken: orderEpoch, connection, datePreset, compare });
  const rest = useRestBlocks({
    refreshToken: orderEpoch,
    connection,
    datePreset,
  });

  useEffect(() => {
    const id = brief.connectionId;
    if (!id || !shouldPullDueOrderDomains(id, refreshToken)) return;
    let cancelled = false;
    void pullDueOrderDomains(id).then(() => {
      if (cancelled) return;
      if (refreshToken > 0) setOrderEpoch((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [refreshToken, brief.connectionId]);

  const connectionOptions = useMemo(
    () => brief.connections.map((c) => ({ id: c.id, label: c.seller_ref || c.id })),
    [brief.connections],
  );

  // Publish connections to the one context strip; never render a second picker.
  useEffect(() => {
    setConnectionOptions(connectionOptions);
    return () => setConnectionOptions([]);
  }, [connectionOptions, setConnectionOptions]);

  // Context strip drives the page, not the other way round.
  useEffect(() => {
    if (filters.connectionId && filters.connectionId !== brief.connectionId) {
      brief.setConnectionId(filters.connectionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follow strip selection only
  }, [filters.connectionId]);

  useEffect(() => {
    if (!brief.model) return;
    setContextLabels({
      sellerLabel: brief.model.sellerRef || undefined,
      marketplace: brief.model.marketplaceIds[0] || undefined,
      connectionStatus:
        brief.model.connectionStatus === 'approved' ||
        brief.model.connectionStatus === 'connected' ||
        brief.model.connectionStatus === 'active'
          ? 'connected'
          : brief.model.connectionStatus === 'error' ||
              brief.model.connectionStatus === 'disconnected'
            ? (brief.model.connectionStatus as 'error' | 'disconnected')
            : 'unknown',
      lastSync: brief.model.lastSuccessfulSync,
    });
    setFilters({
      sellerLabel: brief.model.sellerRef || filters.sellerLabel,
      marketplace: brief.model.marketplaceIds[0] || filters.marketplace,
      connectionId: brief.connectionId || filters.connectionId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- brief load only
  }, [brief.model, brief.connectionId, setContextLabels, setFilters]);

  useEffect(() => {
    if (refreshToken > 0) setLiveMsg('Refreshing your business data.');
  }, [refreshToken]);

  return (
    <div className="founder-dashboard-frame mx-auto w-full max-w-[100rem] space-y-8">
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {liveMsg}
      </div>
      <h1 className="sr-only">Founder dashboard</h1>

      <DebugExportBar
        connectionId={brief.connectionId}
        brief={brief.model}
        kpis={executiveKpis.model}
        overlay={overlay.model}
        pulse={salesPulse.model}
        ops={opsIntel.model}
        map={indiaMap.model}
      />

      <ExecutiveKpiStrip
        model={executiveKpis.model}
        loading={executiveKpis.loading}
        error={executiveKpis.error}
        onRetry={() => {
          void executiveKpis.reload();
        }}
      />

      <DailyBriefPanel
        model={brief.model}
        loading={brief.loading}
        error={brief.error}
        onRetry={() => {
          void brief.reload();
        }}
      />

      <IndiaBusinessMap
        model={indiaMap.model}
        loading={indiaMap.loading}
        error={indiaMap.error}
        onRetry={() => {
          void indiaMap.reload();
        }}
      />

      <OpsIntelligencePanel
        model={opsIntel.model}
        loading={opsIntel.loading}
        error={opsIntel.error}
        onRetry={() => {
          void opsIntel.reload();
        }}
      />

      {brief.model && brief.model.focus.length > 0 && (
        <section aria-label="Today's focus">
          <h2 className="mb-3 text-sm font-semibold text-[var(--shell-fg)]">Today&apos;s focus</h2>
          <FocusList items={brief.model.focus} />
        </section>
      )}

      <MasterOverlayPanel
        model={overlay.model}
        loading={overlay.loading}
        error={overlay.error}
        onRetry={() => {
          void overlay.reload();
        }}
        compareModeLabel={
          COMPARE_OPTIONS.find((c) => c.value === filters.comparePeriod)?.label ??
          filters.comparePeriod
        }
      />

      <SalesPulse24Hours
        model={salesPulse.model}
        loading={salesPulse.loading}
        error={salesPulse.error}
        onRetry={() => {
          void salesPulse.reload();
        }}
      />

      <RestBlocksGrid
        rest={rest.model}
        brief={brief.model}
        kpis={executiveKpis.model}
        ops={opsIntel.model}
        overlay={overlay.model}
        loading={rest.loading}
        error={rest.error}
        onRetry={() => {
          void rest.reload();
        }}
      />
    </div>
  );
}
